import React, { memo, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import FloatingHeader from '../components/layout/FloatingHeader';
import PageMeta from '../components/PageMeta';
import Footer from '../components/Footer';
import { texte, PANELS } from '../components/startseite/texte';
import { starteAbschnitte } from '../components/startseite/abschnitte';
import Lichtszene from '../components/licht/Lichtszene';
import '../components/startseite/startseite.css';
import { SEITEN_BLENDE } from '../components/animations/seitenBlende';

// Support-Seite im neuen Design (Umbau 07.10.2026 nach Davids Feedback: oben und unten nicht doppelt):
// Kopf füllt den ersten Bildschirm — links Suche + Themen-Chips, rechts „Häufigste Fragen“; darunter eine
// Hilfe-Ansicht (Themen-Liste bleibt links stehen, Fragen rechts); ganz unten einmal der Kontakt.
// Fakten (Stand 07.10.2026): Installation über den Installer; After Effects ab 2019 (Manifest AEFT 16.0);
// Mac-Installer nur Apple Silicon; Einzellizenz = 1 Rechner (backend licenses/models.py, single → 1),
// Gerätewechsel über „Lizenz deaktivieren“ im Werkzeug; Abo kündigen über „Verträge hier kündigen“ im Footer (§ 312k BGB)
// oder im Konto („Abo verwalten oder kündigen“).

const MAIL_SUPPORT = 'info@davidmedia.de';
const MAIL_SHOP = 'shop@davidmedia.de';

function inhalte(en) {
  const p = en ? '/en' : '';
  const L = { installer: `${p}/installer`, aio: `${p}/shop/all-in-one`, widerruf: en ? '/en/withdrawal' : '/widerruf' };
  // [id, Titel, Kurztext, Fragen: [Frage, Antwort (Text), Link?]]
  const themen = en ? [
    ['installation', 'Installation', 'Installer, After Effects, systems', [
      ['How do I install the tools?', 'With the free David Media Installer: download it, sign in with your account and install each tool with one click. Then restart After Effects and open it under Window › Extensions.', [L.installer, 'Installer step by step']],
      ['Which After Effects versions and computers are supported?', 'After Effects 2019 or newer. The installer runs on Macs with Apple silicon (M1 or newer) and on Windows.'],
      ['The tool doesn’t show up under Window › Extensions.', 'Quit After Effects completely and start it again. If it still doesn’t appear, check in the installer that the tool shows as installed, then write to us.'],
      ['Can I do without the installer?', 'Yes. Every extension is also available as a single .zxp file in your account after purchase.'],
    ]],
    ['lizenz', 'License & activation', 'Key, computers, switching devices', [
      ['Where do I find my license key?', `In your order confirmation email and in your account under Licenses. Nothing there? Check your spam folder, then write to ${MAIL_SHOP}.`],
      ['On how many computers can I use a tool?', 'One license is valid for one computer.'],
      ['I have a new computer. What now?', 'In the tool, open the license section and click “Deactivate license”, then activate it on the new computer. If the old computer is gone, just write to us.'],
    ]],
    ['updates', 'Updates', 'New versions, what’s included', [
      ['How do I get updates?', 'The installer shows when new versions are available. “Update all” fetches them in one click, then restart After Effects.'],
      ['Are updates included?', 'With the All-in-One you get every update and every new tool. With a single purchase, bug fixes for your tool are included.', [L.aio, 'All-in-One']],
    ]],
    ['kauf', 'Purchase & subscription', 'Cancel, invoice, withdrawal', [
      ['How do I cancel the All-in-One?', 'With the “Cancel contracts here” button at the bottom of every page, no sign-in needed, or in your account under “Manage or cancel subscription”. You can cancel any time, and nothing is charged during the 7-day trial.'],
      ['Do I get an invoice?', `The order confirmation arrives by email. Need an invoice with company details? Write to ${MAIL_SHOP}.`],
      ['Can I return a purchase?', 'Special rules apply to digital content, see the withdrawal policy for details. If something doesn’t work, write to us and we’ll find a solution.', [L.widerruf, 'Withdrawal policy']],
    ]],
  ] : [
    ['installation', 'Installation', 'Installer, After Effects, Systeme', [
      ['Wie installiere ich die Werkzeuge?', 'Mit dem kostenlosen David Media Installer: laden, mit deinem Konto anmelden und jedes Werkzeug mit einem Klick installieren. Danach After Effects neu starten und unter Fenster › Erweiterungen öffnen.', [L.installer, 'Installer Schritt für Schritt']],
      ['Welche After-Effects-Versionen und Rechner werden unterstützt?', 'After Effects ab Version 2019. Der Installer läuft auf Macs mit Apple-Chip (M1 oder neuer) und auf Windows.'],
      ['Das Werkzeug taucht nicht unter Fenster › Erweiterungen auf.', 'After Effects einmal komplett beenden und neu starten. Hilft das nicht, im Installer prüfen, ob das Werkzeug als installiert angezeigt wird, und uns schreiben.'],
      ['Geht es auch ohne Installer?', 'Ja. Jede Erweiterung gibt es nach dem Kauf auch einzeln als .zxp-Datei in deinem Konto.'],
    ]],
    ['lizenz', 'Lizenz & Aktivierung', 'Schlüssel, Rechner, Gerätewechsel', [
      ['Wo finde ich meinen Lizenzschlüssel?', `In der Bestellbestätigung per E-Mail und in deinem Konto unter Lizenzen. Nichts gefunden? Spam-Ordner prüfen, dann an ${MAIL_SHOP} schreiben.`],
      ['Auf wie vielen Rechnern kann ich ein Werkzeug nutzen?', 'Eine Lizenz gilt für einen Rechner.'],
      ['Ich habe einen neuen Rechner. Was jetzt?', 'Im Werkzeug im Lizenz-Bereich auf „Lizenz deaktivieren“ klicken, dann auf dem neuen Rechner aktivieren. Ist der alte Rechner nicht mehr da, schreib uns einfach.'],
    ]],
    ['updates', 'Updates', 'Neue Versionen, was enthalten ist', [
      ['Wie bekomme ich Updates?', 'Der Installer zeigt an, wenn es neue Versionen gibt. „Alle aktualisieren“ holt sie mit einem Klick, danach After Effects neu starten.'],
      ['Sind Updates im Preis enthalten?', 'Im All-in-One bekommst du jedes Update und jedes neue Werkzeug. Beim Einzelkauf sind Fehlerbehebungen für dein Werkzeug enthalten.', [L.aio, 'All-in-One']],
    ]],
    ['kauf', 'Kauf & Abo', 'Kündigen, Rechnung, Widerruf', [
      ['Wie kündige ich das All-in-One?', 'Über den Knopf „Verträge hier kündigen“ unten auf jeder Seite, ohne Anmeldung, oder in deinem Konto unter „Abo verwalten oder kündigen“. Das Abo ist jederzeit kündbar, in den 7 Tagen Test wird nichts abgebucht.'],
      ['Bekomme ich eine Rechnung?', `Die Bestellbestätigung kommt per E-Mail. Brauchst du eine Rechnung mit Firmendaten, schreib an ${MAIL_SHOP}.`],
      ['Kann ich einen Kauf zurückgeben?', 'Für digitale Inhalte gelten besondere Regeln, die Einzelheiten stehen in der Widerrufsbelehrung. Funktioniert etwas nicht, schreib uns, wir finden eine Lösung.', [L.widerruf, 'Widerrufsbelehrung']],
    ]],
  ];
  return { themen, L };
}

const ZEICHEN = {
  installation: <svg viewBox="0 0 24 24"><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M4 20h16" /></svg>,
  lizenz: <svg viewBox="0 0 24 24"><circle cx="8" cy="14" r="4" /><path d="M11 11l9-9" /><path d="M16 6l2 2" /><path d="M19 3l2 2" /></svg>,
  updates: <svg viewBox="0 0 24 24"><path d="M20 11a8 8 0 1 0-2.3 5.7" /><path d="M20 4v7h-7" /></svg>,
  kauf: <svg viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg>,
};

function springe(e, id) {
  const el = document.getElementById(id);
  if (!el) return;
  e.preventDefault();
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' });
}

// Die vier häufigsten Fragen für die Karte oben rechts: [Thema, Index in der Liste]
const BELIEBT = [['installation', 0], ['lizenz', 2], ['lizenz', 0], ['kauf', 0]];

const Inhalt = memo(function Inhalt({ en }) {
  const { themen } = inhalte(en);
  const T = (de, e) => (en ? e : de);
  const thema = id => themen.find(t => t[0] === id);
  return (
    <>
      <section className="sek erste voll" id="support">
        <div className="innen support-kopf">
          <div className="reveal-text">
            <span className="kicker">Support</span>
            <h1 className="als-h2"><span className="zl"><span>{T('Wie können wir helfen?', 'How can we help?')}</span></span><span className="zl"><span className="grau">{T('Antworten sofort, Hilfe vom Menschen.', 'Answers right away, help from a human.')}</span></span></h1>
            <p className="lead reveal">{T('Such nach deiner Frage oder wähl ein Thema. Findest du nichts, schreibst du uns, und wir antworten persönlich.',
              'Search for your question or pick a topic. If you don’t find it, write to us and we’ll answer personally.')}</p>
            <label className="support-suche reveal">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
              <input id="support-suche" type="search" placeholder={T('Frage suchen, z. B. „Lizenz“', 'Search, e.g. “license”')} aria-label={T('Frage suchen', 'Search a question')} />
            </label>
            <div className="themen-chips reveal">
              {themen.map(([id, titel]) => (
                <a key={id} className="chip" href={`#${id}`} onClick={e => springe(e, id)}>
                  <span className="chip-zeichen">{ZEICHEN[id]}</span>{titel}
                </a>
              ))}
            </div>
          </div>
          <div className="beliebt reveal fx">
            <span className="kicker">{T('Häufigste Fragen', 'Most asked')}</span>
            <ul>
              {BELIEBT.map(([id, i]) => {
                const f = thema(id)[3][i];
                return (
                  <li key={f[0]}>
                    <a href={`#frage-${id}-${i}`} data-frage={`frage-${id}-${i}`}>
                      <span className="b-zeichen">{ZEICHEN[id]}</span><span className="b-text">{f[0]}</span><span className="b-pfeil" aria-hidden="true">→</span>
                    </a>
                  </li>
                );
              })}
            </ul>
            <p className="antwortzeit">{T('Nicht dabei? Wir antworten innerhalb von 24 bis 48 Stunden, persönlich.', 'Not there? We answer within 24 to 48 hours, personally.')}</p>
            <span className="fx-licht" /><span className="fx-rand" />
          </div>
        </div>
      </section>

      <section className="sek" id="fragen">
        <div className="innen">
          <div className="zeitlinie"><b /><i /><span>00:00:01:00</span></div>
          <div className="hilfe">
            <nav className="hilfe-nav" aria-label={T('Themen', 'Topics')}>
              <span className="kicker">{T('Themen', 'Topics')}</span>
              {themen.map(([id, titel, , fragen]) => (
                <a key={id} href={`#${id}`} data-nav={id} onClick={e => springe(e, id)}>
                  <span className="chip-zeichen">{ZEICHEN[id]}</span><span>{titel}</span><small>{fragen.length}</small>
                </a>
              ))}
              <a className="hilfe-mail" href={`mailto:${MAIL_SUPPORT}`}>{T('Frage nicht dabei? E-Mail schreiben →', 'Question not here? Send an email →')}</a>
            </nav>
            <div className="hilfe-inhalt">
              {themen.map(([id, titel, kurz, fragen], g) => (
                <div className="faq-gruppe" id={id} key={id}>
                  <div className="gruppe-kopf"><h2>{titel}</h2><span>{kurz}</span></div>
                  <div className="faq">
                    {fragen.map(([f, a, link], i) => (
                      <details key={f} id={`frage-${id}-${i}`} data-suche={`${f} ${a}`.toLowerCase()} open={g === 0 && i === 0}>
                        <summary>{f}</summary>
                        <p>{a}{link && <> <Link to={link[0]}>{link[1]} →</Link></>}</p>
                      </details>
                    ))}
                  </div>
                </div>
              ))}
              <p className="keine-treffer" id="keine-treffer" hidden>
                {T('Dazu haben wir noch keine Antwort.', 'We don’t have an answer for that yet.')} <a href={`mailto:${MAIL_SUPPORT}`}>{T('Schreib uns kurz →', 'Drop us a line →')}</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="sek" id="kontakt-unten">
        <div className="innen">
          <div className="zeitlinie"><b /><i /><span>00:00:02:00</span></div>
          <div className="kontakt-unten">
            <div className="reveal-text">
              <span className="kicker">{T('Kontakt', 'Contact')}</span>
              <h2><span className="zl"><span>{T('Nichts gefunden?', 'Didn’t find it?')}</span></span><span className="zl"><span className="grau">{T('Schreib uns.', 'Write to us.')}</span></span></h2>
              <p className="lead reveal">{T('Beschreib kurz, was du machen wolltest und was passiert ist. Ein Screenshot hilft oft schon. Wir antworten innerhalb von 24 bis 48 Stunden.',
                'Briefly describe what you wanted to do and what happened. A screenshot often helps. We reply within 24 to 48 hours.')}</p>
            </div>
            <div className="mail-karten">
              <a className="mail-karte reveal fx" href={`mailto:${MAIL_SUPPORT}`}>
                <span className="kicker">{T('Allgemeiner Support', 'General support')}</span>
                <b>{MAIL_SUPPORT}</b>
                <small>{T('Installation, Lizenz, Fragen zu den Werkzeugen', 'Installation, licenses, questions about the tools')}</small>
                <span className="fx-licht" /><span className="fx-rand" />
              </a>
              <a className="mail-karte reveal fx" href={`mailto:${MAIL_SHOP}`} style={{ transitionDelay: '80ms' }}>
                <span className="kicker">{T('Bestellungen & Rechnungen', 'Orders & invoices')}</span>
                <b>{MAIL_SHOP}</b>
                <small>{T('Kauf, Abo, Rechnung, Lizenzschlüssel', 'Purchase, subscription, invoice, license key')}</small>
                <span className="fx-licht" /><span className="fx-rand" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
});

export default function Support() {
  const location = useLocation();
  const en = location.pathname.startsWith('/en/') || location.pathname === '/en';
  const wurzel = useRef(null);
  const { themen } = useMemo(() => inhalte(en), [en]);

  // Fragen auch als Strukturdaten, damit Google sie direkt unter dem Suchergebnis zeigen kann.
  // Aus derselben Liste gebaut, die die Seite zeigt.
  const faqSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: themen.flatMap(([, , , fragen]) => fragen).map(([f, a]) => ({
      '@type': 'Question', name: f, acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  }), [themen]);

  useEffect(() => {
    const T = texte(en);
    return starteAbschnitte({ wurzel: wurzel.current, T: T.szene, panels: PANELS, preise: T.preise });
  }, [en]);

  // Suche, „Häufigste Fragen“ und die mitlaufende Themen-Liste (direkt am DOM, der Inhalt rendert nicht neu)
  useEffect(() => {
    const el = wurzel.current;
    const suche = el.querySelector('#support-suche');
    const fragen = [...el.querySelectorAll('.faq-gruppe details')];
    let gesprungen = false;
    const oeffne = id => {
      const d = el.querySelector('#' + id); if (!d) return;
      d.open = true;
      window.scrollTo({ top: d.getBoundingClientRect().top + window.scrollY - 120, behavior: 'smooth' });
    };
    const beiSuche = () => {
      const q = suche.value.trim().toLowerCase();
      let treffer = 0;
      fragen.forEach(d => {
        const passt = !q || d.dataset.suche.includes(q);
        d.hidden = !passt;
        if (passt) treffer++;
        if (q && passt) d.open = true;
      });
      el.querySelectorAll('.faq-gruppe').forEach(g => { g.hidden = !!q && !g.querySelector('details:not([hidden])'); });
      el.querySelector('#keine-treffer').hidden = !q || treffer > 0;
      if (q && !gesprungen) {
        gesprungen = true;
        const ziel = el.querySelector('#fragen');
        window.scrollTo({ top: ziel.getBoundingClientRect().top + window.scrollY - 40, behavior: 'smooth' });
      }
      if (!q) gesprungen = false;
    };
    suche.addEventListener('input', beiSuche);
    const beiKlick = e => {
      const a = e.target.closest && e.target.closest('[data-frage]');
      if (!a) return;
      e.preventDefault();
      oeffne(a.dataset.frage);
    };
    el.addEventListener('click', beiKlick);
    const navLinks = [...el.querySelectorAll('[data-nav]')];
    const io = new IntersectionObserver(es => es.forEach(e => {
      if (e.isIntersecting) navLinks.forEach(l => l.classList.toggle('an', l.dataset.nav === e.target.id));
    }), { rootMargin: '-35% 0px -60% 0px' });
    el.querySelectorAll('.faq-gruppe').forEach(g => io.observe(g));
    return () => { suche.removeEventListener('input', beiSuche); el.removeEventListener('click', beiKlick); io.disconnect(); };
  }, [en]);

  return (
    <motion.div {...SEITEN_BLENDE}>
      <PageMeta
        title="Support & FAQ | DAVIDMEDIA"
        description={en
          ? 'Help with the David Media tools for After Effects: installation, license and activation, updates, subscription and how to reach support.'
          : 'Hilfe zu den David-Media-Werkzeugen für After Effects: Installation, Lizenz und Aktivierung, Updates, Abo und Kontakt zum Support.'}
        url={`https://www.davidmedia.de${en ? '/en' : ''}/support`}
        schema={faqSchema}
      />
      <FloatingHeader />
      <div className="startseite" ref={wurzel}>
        <Lichtszene />
        <Inhalt en={en} />
      </div>
      <div className="relative z-10">
        <Footer />
      </div>
    </motion.div>
  );
}
